import {
  Box,
  TextField as TextInput,
  BaseTextFieldProps,
  AutocompleteRenderInputParams,
  SlotProps,
  TextFieldOwnerState,
  InputBaseProps,
} from '@mui/material';
import { theme } from '../../configs/theme';
import { ChangeEvent, ReactNode } from 'react';
import { TextWrapper, TextWrapperProps } from './TextWrapper';
import ConditionalWrapper from '../wrappers/ConditionalWrapper';

/**
 * Props for the TextField component.
 */
type TextFieldProps = BaseTextFieldProps &
  Partial<Pick<AutocompleteRenderInputParams, 'InputProps'>> & {
    /**
     * Callback function triggered when the value changes.
     * @param {ChangeEvent<unknown>} e - The change event object.
     */
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;

    /**
     * An optional icon to display inside the input field.
     */
    icon?: ReactNode;

    /**
     * The alignment of the icon in the input field.
     * Options: `'left'` or `'right'`.
     */
    iconAlign?: 'left' | 'right';

    /**
     * If true, displays a red asterisk (*) next to the label to indicate a required field.
     */
    required?: boolean;

    /**
     * If true, the input field will be read-only.
     */
    readOnly?: boolean;

    /**
     * Additional styles for the input container.
     */
    sx?: object;

    /**
     * Is there an error in the input field?
     */
    error?: boolean;

    /**
     * Text to display below if error.
     */
    errorText?: string;

    inputType?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'url';

    wrapperProps?: Omit<TextWrapperProps, 'children'>;
  };

/**
 * A customizable text input component built on Material-UI's TextField.
 *
 * @param {TextFieldProps} props - The props for the TextField component.
 * @returns {ReactElement} The rendered TextField component.
 *
 * @example
 * <TextField
 *    placeholder="Enter your text"
 *    name="exampleInput"
 *    value="Hello"
 *    onChange={(e) => console.log(e.target.value)}
 *    icon={<SearchIcon />}
 *    iconAlign="left"
 *    multiline
 *    required={true}
 *    readOnly
 * />
 */

export const TextField = ({
  icon,
  iconAlign = 'right',
  sx = { width: '100%' },
  error = false,
  errorText,
  inputType = 'text',
  wrapperProps,
  ...props
}: TextFieldProps) => {
  return (
    <ConditionalWrapper
      condition={!!wrapperProps}
      wrapper={(children) => (
        <TextWrapper {...wrapperProps} required={props.required}>
          {children}
        </TextWrapper>
      )}
    >
      <Box width={'100%'} display={'flex'} flexDirection={'column'}>
        <TextInput
          {...props}
          rows={props.multiline ? 3 : 1}
          type={inputType}
          onBlur={props.onBlur}
          onKeyDown={props.onKeyDown}
          sx={{
            ...sx,
            opacity: props.disabled ? 0.6 : 1,
            pointerEvents: props.disabled ? 'none' : 'auto',
            border: '1px solid grey',
            borderRadius: '8px',
            backgroundColor: theme.palette.customColors.inputBackground,
          }}
          variant="standard"
          slotProps={{
            input: {
              readOnly: props.readOnly || props.disabled,
              disableUnderline: true,
              startAdornment: icon && iconAlign === 'left' ? icon : null,
              endAdornment: icon && iconAlign === 'right' ? icon : null,
              style: {
                border: 'none',
                borderRadius: '8px',
                padding: props.multiline ? '11px' : '8px',
                outline: error ? `1px solid ${theme.palette.status.rejected}` : 'none',
                backgroundColor: props.disabled
                  ? theme.palette.action.disabledBackground
                  : theme.palette.customColors.inputBackground,
              },
              ...(props.inputProps as SlotProps<
                React.ElementType<InputBaseProps>,
                {},
                TextFieldOwnerState
              >),
              ...props.InputProps,
              onChange: props.onChange,
            },
          }}
        />
        {error && (
          <p style={{ color: theme.palette.status.rejected, fontSize: '12px', marginTop: 6 }}>
            {errorText}
          </p>
        )}
      </Box>
    </ConditionalWrapper>
  );
};
